
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, App, InputNumber, Switch } from 'antd';
import { createMembershipType, updateMembershipType, getMembershipTypeById } from '../../services/memberships';

const { useApp } = App;
const { TextArea } = Input;

const MembershipTypeUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getMembershipTypeById(id)
      .then((membershipType) => {
        form.setFieldsValue({
          name: membershipType.data.name,
          description: membershipType.data.description || '',
          price: membershipType.data.price,
          durationDays: membershipType.data.durationDays,
          isActive: membershipType.data.isActive,
        });
      })
      .catch(() => {
        message.error('No se pudo cargar el tipo de membresía');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      if (isEdit && id) {
        const updateData = {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          price: values.price,
          durationDays: values.durationDays,
          isActive: values.isActive,
        };

        await updateMembershipType(id, updateData);
        message.success('Tipo de membresía actualizado correctamente');
      } else {
        const createData = {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          price: values.price,
          durationDays: values.durationDays,
          isActive: values.isActive,
        };

        await createMembershipType(createData);
        message.success('Tipo de membresía creado correctamente');
      }

      navigate('/memberships/all');
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el tipo de membresía';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card title={isEdit ? 'Editar tipo de membresía' : 'Crear tipo de membresía'} loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
          initialValues={{
            isActive: true,
            price: 0,
            durationDays: 30,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nombre"
                name="name"
                rules={[
                  { required: true, message: 'El nombre es requerido' },
                  { min: 2, message: 'Mínimo 2 caracteres' },
                  { max: 100, message: 'Máximo 100 caracteres' },
                ]}
              >
                <Input placeholder="Ej: Básica, Premium, Empresarial" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label="Descripción"
                name="description"
                rules={[
                  { max: 500, message: 'Máximo 500 caracteres' },
                ]}
              >
                <TextArea
                  placeholder="Describe las características de esta membresía"
                  rows={3}
                  allowClear
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Precio"
                name="price"
                rules={[
                  { required: true, message: 'El precio es requerido' },
                  { type: 'number', min: 0, message: 'El precio debe ser mayor o igual a 0' },
                ]}
              >
                <InputNumber
                  placeholder="0.00"
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Duración (días)"
                name="durationDays"
                rules={[
                  { required: true, message: 'La duración es requerida' },
                  { type: 'number', min: 1, message: 'La duración debe ser al menos 1 día' },
                ]}
              >
                <InputNumber
                  placeholder="30"
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Activo"
                name="isActive"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? 'Guardar cambios' : 'Crear'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default MembershipTypeUpsertPage;